<script setup lang="ts">
import LawyerCardSkeleton from '@/pages/lawyer/ui/Skeleton/LawyerCardSkeleton.vue';
import TimeSectionSkeleton from '@/pages/lawyer/ui/Skeleton/TimeSectionSkeleton.vue';
import NavigationLinksSkeleton from '@/pages/lawyer/ui/Skeleton/NavigationLinksSkeleton.vue';
import MainSectionSkeleton from '@/pages/lawyer/ui/Skeleton/MainSectionSkeleton.vue';
import ExperienceSectionSkeleton from '@/pages/lawyer/ui/Skeleton/ExperienceSectionSkeleton.vue';
import FeedbacksSectionSkeleton from '@/pages/lawyer/ui/Skeleton/FeedbacksSectionSkeleton.vue';
import ContactSectionSkeleton from '@/pages/lawyer/ui/Skeleton/ContactSectionSkeleton.vue';
import ServicesSectionSkeleton from '@/pages/lawyer/ui/Skeleton/ServicesSectionSkeleton.vue';
import LawyerSliderSkeleton from '@/pages/lawyer/ui/Skeleton/LawyerSliderSkeleton.vue';
import { useUserStore } from '@/entities/user/store';

const route = useRoute();
const userStore = useUserStore();

const isMobile: boolean = useIsMobile();

const { isAuth, isRoleTypeUser } = storeToRefs(userStore);
const isCompany: ComputedRef<boolean> = computed(() =>
  String(route.name).includes('organizations'),
);
</script>

<template>
  <div :class="['lawyer-page-skeleton', { _mobile: isMobile }]">
    <div class="lawyer-page-skeleton__breadcrumbs"></div>
    <div class="lawyer-page-skeleton__main">
      <aside class="lawyer-page-skeleton__sidebar">
        <div class="lawyer-page-skeleton__sidebar-lawyer">
          <LawyerCardSkeleton />
        </div>
        <div v-if="isRoleTypeUser || !isAuth" class="lawyer-page-skeleton__sidebar-time">
          <TimeSectionSkeleton />
        </div>
      </aside>
      <div class="lawyer-page-skeleton__sections">
        <div class="lawyer-page-skeleton__navigation">
          <div v-if="!isMobile" class="lawyer-page-skeleton__navigation-links">
            <NavigationLinksSkeleton />
          </div>
          <div class="lawyer-page-skeleton__navigation-sections">
            <div class="lawyer-page-skeleton__navigation-section">
              <MainSectionSkeleton class="lawyer-section" :is-company="isCompany" />
            </div>
            <div class="lawyer-page-skeleton__navigation-section">
              <ExperienceSectionSkeleton class="lawyer-section" />
            </div>
            <div class="lawyer-page-skeleton__navigation-section">
              <FeedbacksSectionSkeleton class="lawyer-section" />
            </div>
            <div class="lawyer-page-skeleton__navigation-section">
              <ContactSectionSkeleton class="lawyer-section" />
            </div>
            <div class="lawyer-page-skeleton__navigation-section">
              <ServicesSectionSkeleton class="lawyer-section" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="lawyer-page-skeleton__slider">
      <LawyerSliderSkeleton />
    </div>
  </div>
</template>

<style lang="less" scoped>
.lawyer-page-skeleton {
  &__breadcrumbs {
    margin-top: 30px;
    margin-bottom: 50px;
    min-height: 27px;
  }

  &__main {
    display: flex;
    margin-bottom: 59px;
  }

  &__sidebar {
    max-width: 453px;
    width: 31.5%;
    margin-right: 65px;
    flex: 1 1 auto;

    @media @1280 {
      width: 35.3%;
    }

    @media @tablet {
      margin-right: 30px;
    }
  }

  &__sidebar-time {
    top: @size-x5;
    margin-top: 48px;
    position: sticky;
  }

  &__sections {
    position: relative;
    max-width: 681px;
    width: 68.5%;

    @media @1280 {
      width: 64.7%;
    }
  }

  &__navigation-links {
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 5;
  }

  &__navigation-sections {
    margin-top: @size-x6;
  }

  &__navigation-section {
    margin-bottom: 48px;
  }

  &._mobile &__breadcrumbs {
    margin-top: 22px;
    margin-bottom: @size-x6;
  }

  &._mobile &__main {
    display: block;
    margin-bottom: 0;
  }

  &._mobile &__sidebar {
    width: 100%;
    max-width: 100%;
    margin-right: 0;
  }

  &._mobile &__sidebar-time {
    margin-top: @size-x7;
  }

  &._mobile &__sections {
    width: 100%;
    max-width: 100%;
    margin-top: @size-x7;
  }
}
</style>
